/**
 * SchemaList - Database Schema Builder
 *
 * Visual database builder for creating user-defined tables in MyVault.
 * Replaces the previous read-only schema list with an interactive builder.
 */

import React, { useEffect, useCallback, useRef, useState } from "react";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Typography from "@mui/joy/Typography";
import Alert from "@mui/joy/Alert";
import CircularProgress from "@mui/joy/CircularProgress";
import Snackbar from "@mui/joy/Snackbar";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import DialogTitle from "@mui/joy/DialogTitle";
import DialogContent from "@mui/joy/DialogContent";
import DialogActions from "@mui/joy/DialogActions";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import { useSchemaStatus, useSchemaBuilder } from "../hooks";
import {
  SchemaBuilderProvider,
  useSchemaBuilderContext,
  WelcomeEmptyState,
  TableListPanel,
  TableEditor,
} from "../components/schemaBuilder";
import { FloatingChatWidget } from "../components/MyVaultAgent";
import type {
  SchemaBuilderColumn,
  CreateTableRequest,
  ColumnDefinition,
  SimpleColumnType,
  ApiColumnType,
} from "../types";
import {
  columnsToAdd,
  deleteBuilderColumn,
  withPersistedColumnIds,
} from "./schemaBuilderPersistence";

/**
 * Main SchemaList component - wraps content in the provider
 */
export default function SchemaList() {
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";

  if (!workspaceId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="warning">Please select a workspace first.</Alert>
      </Box>
    );
  }

  return (
    <SchemaBuilderProvider>
      <SchemaBuilderContent workspaceId={workspaceId} />
    </SchemaBuilderProvider>
  );
}

/**
 * Inner content component that uses the schema builder context
 */
function SchemaBuilderContent({ workspaceId }: { workspaceId: string }) {
  const {
    state,
    addTable,
    updateTable,
    deleteTable,
    selectTable,
    addColumn,
    updateColumn,
    deleteColumn,
    reorderColumns,
    addRelationship,
    deleteRelationship,
    loadTables,
    loadRelationships,
    setDirty,
    setSaving,
    setError,
  } = useSchemaBuilderContext();

  const {
    status,
    loading: statusLoading,
    error: statusError,
    refresh: refreshStatus,
  } = useSchemaStatus(workspaceId);

  const schemaBuilder = useSchemaBuilder(workspaceId);

  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingColumnIds, setDeletingColumnIds] = useState<Set<string>>(
    new Set(),
  );
  const deletingColumnIdsRef = useRef<Set<string>>(new Set());

  // Load existing tables when status is available (only once)
  useEffect(() => {
    const loadExistingData = async () => {
      if (
        status?.is_initialized &&
        status.table_count > 0 &&
        !dataLoaded &&
        !isLoadingData
      ) {
        try {
          setIsLoadingData(true); // Show loading spinner
          const { tables, relationships } =
            await schemaBuilder.fetchExistingTables();
          loadTables(tables);
          loadRelationships(relationships);
          setDataLoaded(true); // Mark as loaded to prevent re-fetching
        } catch (err) {
          console.error("Failed to load existing tables:", err);
          setError(
            err instanceof Error
              ? err.message
              : "Failed to load existing tables",
          );
        } finally {
          setIsLoadingData(false);
        }
      }
    };
    loadExistingData();
  }, [
    status,
    dataLoaded,
    isLoadingData,
    schemaBuilder,
    loadTables,
    loadRelationships,
    setError,
  ]);

  // Get the selected table
  const selectedTable = state.tables.find(
    (t) => t.tempId === state.selectedTableId,
  );

  /**
   * Handle creating the first table (also initializes schema if needed)
   */
  const handleCreateFirstTable = useCallback(async () => {
    setIsInitializing(true);
    try {
      // Initialize schema if not already done
      if (!status?.is_initialized) {
        await schemaBuilder.initializeSchema();
        await refreshStatus();
      }
      // Add a new table to the local state
      addTable();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to initialize schema",
      );
    } finally {
      setIsInitializing(false);
    }
  }, [status, schemaBuilder, refreshStatus, addTable, setError]);

  /**
   * Handle deleting a table - calls backend immediately for saved tables
   */
  const handleDeleteTable = useCallback(
    async (tempId: string) => {
      const table = state.tables.find((t) => t.tempId === tempId);
      if (!table) return;

      if (table.id) {
        // Saved table: drop from backend first
        try {
          await schemaBuilder.dropTable(table.id, true);
        } catch (err) {
          setError(
            err instanceof Error ? err.message : "Failed to delete table",
          );
          return;
        }
      }

      deleteTable(tempId);
    },
    [state.tables, schemaBuilder, deleteTable, setError],
  );

  const handleDeleteColumn = useCallback(
    async (columnId: string) => {
      if (!selectedTable) return;
      const column = selectedTable.columns.find(
        (candidate) => candidate.tempId === columnId,
      );
      if (!column) return;

      await deleteBuilderColumn(
        selectedTable.tempId,
        column,
        schemaBuilder.dropColumn,
        deleteColumn,
        setError,
        deletingColumnIdsRef.current,
        (tempColumnId, pending) => {
          setDeletingColumnIds((current) => {
            const next = new Set(current);
            if (pending) next.add(tempColumnId);
            else next.delete(tempColumnId);
            return next;
          });
        },
      );
    },
    [selectedTable, schemaBuilder.dropColumn, deleteColumn, setError],
  );

  /**
   * Handle adding a new table
   */
  const handleAddTable = useCallback(() => {
    addTable();
  }, [addTable]);

  /**
   * Convert UI state to API request format
   */
  const convertToColumnDefinition = (
    column: SchemaBuilderColumn,
  ): ColumnDefinition => ({
    name: column.name || `column_${column.sortOrder}`,
    display_name:
      column.displayName || column.name || `Column ${column.sortOrder}`,
    simple_type: mapColumnType(column.dataType),
    is_nullable: !column.isRequired,
    is_primary_key: column.isPrimaryKey,
    select_options: column.selectOptions,
  });

  const mapColumnType = (type: SimpleColumnType): ApiColumnType => {
    switch (type) {
      case "Text":
        return "TEXT";
      case "Number":
        return "NUMBER";
      case "Date":
        return "DATE";
      case "Yes/No":
        return "BOOLEAN";
      case "Selection":
        return "SELECT";
      default:
        return "TEXT";
    }
  };

  /**
   * Save all changes to the backend
   */
  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSaveSuccess(false);

    try {
      // Process each table
      for (const table of state.tables) {
        if (!table.id) {
          // Create new table with all its columns
          const request: CreateTableRequest = {
            name: table.name || `table_${Date.now()}`,
            display_name: table.displayName || "Untitled Table",
            description: table.description || undefined,
            columns: table.columns.map(convertToColumnDefinition),
          };

          const created = await schemaBuilder.createTable(request);
          // Keep the server-assigned column IDs so later saves do not add them again.
          updateTable(table.tempId, {
            id: created.id,
            columns: withPersistedColumnIds(table.columns, created.columns),
          });
        } else {
          // Update existing table metadata
          await schemaBuilder.alterTable(table.id, {
            display_name: table.displayName,
            description: table.description || undefined,
          });

          // Add unsaved columns and retain each ID returned by the API.
          let columnsWithIds = table.columns;
          for (const column of columnsToAdd(table.columns)) {
            const createdColumn = await schemaBuilder.addColumn(table.id, {
              column: convertToColumnDefinition(column),
            });
            columnsWithIds = withPersistedColumnIds(columnsWithIds, [
              createdColumn,
            ]);
            updateTable(table.tempId, { columns: columnsWithIds });
          }
        }
      }

      // Process relationships
      for (const rel of state.relationships) {
        if (!rel.id) {
          // Find actual table/column IDs
          const sourceTable = state.tables.find(
            (t) => t.tempId === rel.sourceTableId,
          );
          const targetTable = state.tables.find(
            (t) => t.tempId === rel.targetTableId,
          );
          const sourceColumn = sourceTable?.columns.find(
            (c) => c.tempId === rel.sourceColumnId,
          );
          const targetColumn = targetTable?.columns.find(
            (c) => c.tempId === rel.targetColumnId,
          );

          if (
            sourceTable?.id &&
            targetTable?.id &&
            sourceColumn &&
            targetColumn
          ) {
            await schemaBuilder.createRelationship({
              from_table_id: sourceTable.id,
              from_column_name: sourceColumn.name,
              to_table_id: targetTable.id,
              to_column_name: targetColumn.name,
              on_delete: rel.onDelete,
            });
          }
        }
      }

      setDirty(false);
      setSaveSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save changes");
    } finally {
      setSaving(false);
    }
  }, [
    state.tables,
    state.relationships,
    schemaBuilder,
    updateTable,
    setSaving,
    setDirty,
    setError,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  // Show loading state while fetching schema status OR loading existing tables
  // Also show spinner when we know tables exist but haven't loaded them yet
  const needsToLoadTables =
    status?.is_initialized && status.table_count > 0 && !dataLoaded;
  if (statusLoading || isLoadingData || needsToLoadTables) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (statusError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="danger">{statusError}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography level="h3">Database</Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {state.error && (
            <Alert color="danger" size="sm" sx={{ py: 0.5 }}>
              {state.error}
            </Alert>
          )}
          {state.isDirty && (
            <Typography level="body-sm" sx={{ color: "warning.500" }}>
              Unsaved changes
            </Typography>
          )}
          {selectedTable && (
            <Button
              variant="soft"
              color="danger"
              startDecorator={<DeleteRoundedIcon />}
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete Table
            </Button>
          )}
          <Button
            startDecorator={<SaveRoundedIcon />}
            onClick={handleSave}
            loading={state.isSaving}
            disabled={!state.isDirty || state.tables.length === 0}
          >
            Save
          </Button>
        </Box>
      </Box>

      {/* Main content */}
      {state.tables.length === 0 ? (
        <WelcomeEmptyState
          onCreateTable={handleCreateFirstTable}
          isInitializing={isInitializing}
        />
      ) : (
        <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
          <TableListPanel
            tables={state.tables}
            selectedTableId={state.selectedTableId}
            onSelectTable={selectTable}
            onAddTable={handleAddTable}
            onDeleteTable={handleDeleteTable}
          />
          {selectedTable ? (
            <TableEditor
              table={selectedTable}
              allTables={state.tables}
              relationships={state.relationships}
              onUpdateTable={(updates) =>
                updateTable(selectedTable.tempId, updates)
              }
              onAddColumn={() => addColumn(selectedTable.tempId)}
              onUpdateColumn={(columnId, updates) =>
                updateColumn(selectedTable.tempId, columnId, updates)
              }
              onDeleteColumn={handleDeleteColumn}
              deletingColumnIds={deletingColumnIds}
              onReorderColumns={(columns) =>
                reorderColumns(selectedTable.tempId, columns)
              }
              onAddRelationship={addRelationship}
              onDeleteRelationship={deleteRelationship}
            />
          ) : (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography level="body-lg" sx={{ color: "text.tertiary" }}>
                Select a table to edit
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Delete Table Confirmation Modal */}
      <Modal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
      >
        <ModalDialog variant="outlined" role="alertdialog">
          <DialogTitle>
            <WarningRoundedIcon />
            Delete Table
          </DialogTitle>
          <DialogContent>
            Are you sure you want to delete &quot;
            {selectedTable?.displayName || "this table"}&quot;?
            {selectedTable?.id && (
              <Typography level="body-sm" sx={{ mt: 1, color: "danger.500" }}>
                Warning: This table has been saved to the database. Deleting it
                will permanently remove all data stored in this table.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              variant="solid"
              color="danger"
              onClick={async () => {
                if (selectedTable) {
                  await handleDeleteTable(selectedTable.tempId);
                }
                setShowDeleteConfirm(false);
              }}
            >
              Delete
            </Button>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setShowDeleteConfirm(false)}
            >
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Success snackbar */}
      <Snackbar
        open={saveSuccess}
        autoHideDuration={3000}
        onClose={() => setSaveSuccess(false)}
        color="success"
        variant="soft"
        startDecorator={<CheckCircleRoundedIcon />}
      >
        Changes saved successfully
      </Snackbar>

      {/* MyVault Agent Chat Widget */}
      <FloatingChatWidget workspaceId={workspaceId} />
    </Box>
  );
}
